import { createAction } from '@reduxjs/toolkit';

import type { UserTypes } from '@modules';
import api from '@modules/api';
import { createActions, createAsyncActions } from '@modules/helpers';

import { TBranchData } from './types';

export const actions = createActions('user', {
  setCaptcha: name => createAction<string>(name),

  clearErrors: name => createAction<void>(name),

  skipKYC: name => createAction<void>(name),

  initBranch: name => createAction<{ error?: string; data: TBranchData }>(name),

  setIsCountry: name => createAction<boolean>(name),

  setIsIntroModal: name => createAction<boolean>(name),

  setStrongPasswordModal: name => createAction<boolean>(name),

  clearKYCToken: name => createAction<void>(name),

  setIbanStatus: name => createAction<void>(name),
});

export const asyncActions = createAsyncActions('user', {
  editUser: (data: UserTypes.EditUserParams, thunkApi) =>
    api.post<UserTypes.EditUserResponse>(thunkApi, '/user22/edit', data),

  toggleStrongSecurity: (data: UserTypes.ToggleStrongSecurityParams, thunkApi) => {
    const url = '/user22/toggle-strong-security';
    return api.post<UserTypes.ToggleStrongSecurityResponse>(thunkApi, url, data);
  },

  getUserData: (_: void, thunkApi) => {
    const url = '/user22/data';
    return api.get<UserTypes.GetUserDataResponse>(thunkApi, url);
  },

  getUserDataPolling: (_: void, thunkApi) => {
    const url = '/user22/data';
    return api.get<UserTypes.GetUserDataResponse>(thunkApi, url);
  },

  changeEmailSendOldEmail: (data: UserTypes.SendOldEmailParams, thunkApi) => {
    const url = '/user22/change-email/send-old-email';
    return api.post<UserTypes.SendOldEmailResponse>(thunkApi, url, data);
  },

  changeEmailVerifyOldEmail: (data: UserTypes.ChangeEmailVerifyEmailParams, thunkApi) => {
    const url = '/user22/change-email/verify-old-email';
    return api.post<UserTypes.ChangeEmailVerifyOldEmailResponse>(thunkApi, url, data);
  },
  changeEmailVerifyPassword: (data: UserTypes.ChangeEmailVerifyPasswordParams, thunkApi) => {
    const url = '/user22/change-email/verify-password';
    return api.post<UserTypes.ChangeEmailVerifyPasswordResponse>(thunkApi, url, data);
  },

  changeEmailSendNewEmail: (data: UserTypes.ChangeEmailSendNewEmailParams, thunkApi) => {
    const url = '/user22/change-email/send-new-email';
    return api.post<UserTypes.ChangeEmailSendNewEmailResponse>(thunkApi, url, data);
  },

  changeEmailVerifyEmail: (data: UserTypes.ChangeEmailVerifyEmailParams, thunkApi) => {
    const url = '/user22/change-email/verify-email';
    return api.post<UserTypes.ChangeEmailVerifyEmailResponse>(thunkApi, url, data);
  },

  changeEmailVerifyOTP: (data: UserTypes.ChangeEmailVerifyOTPParams, thunkApi) => {
    const url = '/user22/change-email/verify-otp';
    return api.post<UserTypes.ChangeEmailVerifyEmailResponse>(thunkApi, url, data);
  },
  changePhoneVerifyOTP: (data: UserTypes.ChangeEmailVerifyOTPParams, thunkApi) => {
    const url = '/user22/change-phone/verify-otp';
    return api.post<UserTypes.ChangeEmailVerifyEmailResponse>(thunkApi, url, data);
  },

  changeEmailVerifyPhone: (data: UserTypes.ChangeEmailVerifyPhoneParams, thunkApi) => {
    const url = '/user22/change-email/verify-phone';
    return api.post<UserTypes.ChangeEmailVerifyPhoneResponse>(thunkApi, url, data);
  },

  changePhone: (data: UserTypes.ChangePhoneParams, thunkApi) => {
    const url = '/user22/change-phone';
    return api.post<UserTypes.ChangePhoneResponse>(thunkApi, url, data);
  },

  changePhoneVerifyEmail: (data: UserTypes.ChangePhoneVerifyEmailParams, thunkApi) => {
    const url = '/user22/change-phone/verify-email';
    return api.post<UserTypes.ChangePhoneVerifyEmailResponse>(thunkApi, url, data);
  },

  changePhoneVerifyPhone: (data: UserTypes.ChangePhoneVerifyPhoneParams, thunkApi) => {
    const url = '/user22/change-phone/verify-phone';
    return api.post<UserTypes.ChangePhoneVerifyPhoneResponse>(thunkApi, url, data);
  },

  sendDocumentCredentials: (data: UserTypes.SetDocumentCredentialsParams, thunkApi) => {
    const url = '/user22/document-credentials';
    return api.post<UserTypes.UserEmptyResponse>(thunkApi, url, data);
  },

  changePassword: (data: UserTypes.ChangePasswordParams, thunkApi) => {
    const url = '/user22/change-password';
    return api.post<UserTypes.ChangePasswordResponse>(thunkApi, url, data);
  },

  changePasswordVerifyPhone: (data: UserTypes.ChangePasswordVerifyPhoneParams, thunkApi) => {
    const url = '/user22/change-password/verify-phone';
    return api.post<UserTypes.UserEmptyResponse>(thunkApi, url, data);
  },

  changePasswordVerifyEmail: (data: UserTypes.ChangePasswordVerifyEmailParams, thunkApi) => {
    const url = '/user22/change-password/verify-email';
    return api.post<UserTypes.ChangePasswordSendEmailResponse>(thunkApi, url, data);
  },

  checkDocuments: (data: UserTypes.CheckDocumentsParams, thunkApi) => {
    const url = '/user22/check-document';
    return api.get<UserTypes.UserEmptyResponse>(thunkApi, url, { params: data });
  },

  accessKYCToken: (data: UserTypes.AccessKYCTokenParams | void, thunkApi) => {
    const url = '/user22/kyc-access-token';
    return api.get<UserTypes.AccessKYCTokenResponse>(thunkApi, url, { params: data });
  },

  zendeskVerification: (_: void, thunkApi) => {
    const url = '/verification/zendesk';
    return api.get<UserTypes.VerificationZendeskResponse>(thunkApi, url);
  },

  changeNewPassword: (data: UserTypes.ChangeNewPasswordParams, thunkApi) => {
    const url = '/user22/change-password/set-password';
    return api.post<UserTypes.ChangeNewPasswordResponse>(thunkApi, url, data);
  },

  changePasswordSendEmail: (data: UserTypes.ChangePasswordSendEmailParams, thunkApi) => {
    const url = '/user22/change-password/send-email';
    return api.post<UserTypes.ChangePasswordSendEmailResponse>(thunkApi, url, data);
  },

  changePasswordSetPassword: (data: UserTypes.ChangePasswordSetPasswordParams, thunkApi) => {
    const url = '/user22/change-password/set-password';
    return api.post<UserTypes.ChangePasswordSetPasswordlResponse>(thunkApi, url, data);
  },

  getKYCStatus: (_: void, thunkApi) => {
    const url = '/user22/kyc-status';
    return api.get<UserTypes.UserKYCStatusResponse>(thunkApi, url);
  },

  getZHAgreement: (_: void, thunkApi) => {
    const url = '/user22/zero-hash-agreement';
    return api.get<UserTypes.UserZHAgreement>(thunkApi, url);
  },

  getZHDisclaimer: (_: void, thunkApi) => {
    const url = '/user22/zero-hash-disclaimer';
    return api.get<UserTypes.UserZHDisclaimer>(thunkApi, url);
  },

  getOTPSetup: (_: void, thunkApi) => {
    const url = '/otp/setup';
    return api.get<UserTypes.OTPSetup>(thunkApi, url);
  },
  confirmOTP: (data: UserTypes.OTPConfirm, thunkApi) => {
    const url = '/otp/confirm';
    return api.post<void>(thunkApi, url, data);
  },
  verifyOTP: (data: UserTypes.OTPVerify, thunkApi) => {
    const url = '/otp/verify';
    return api.post<void>(thunkApi, url, data);
  },

  getKycAddress: (_: void, thunkApi) => {
    const url = 'user22/kyc-address';
    return api.get<UserTypes.GetKycAddressResponse>(thunkApi, url);
  },
});

export default { ...actions, ...asyncActions };
